<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid
      :desktop-default-grid="[1, 22]"
      :tablet-default-grid="[1, 14]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-workflow-bio__inner">
        <JssRichText class="c-workflow-bio__-title" :field="fields?.title" tag="h2" />

        <div v-if="months.length || phases.length" class="c-workflow-bio__chart">
          <div v-if="months.length" class="c-workflow-bio__months" :style="timelineStyle">
            <template v-for="(month, index) in months" :key="month.id || month.fields.label?.value">
              <span
                v-if="index === 0"
                class="c-workflow-bio__month-line c-workflow-bio__month-line--edge"
                aria-hidden="true"
              ></span>
              <span
                class="c-workflow-bio__month"
                :class="getVariantClass(month.fields.colorVariant?.value, 'month')"
                :style="getMonthStyle(month)"
              >
                {{ month.fields.label.value }}
              </span>
              <span
                v-if="index < months.length - 1"
                class="c-workflow-bio__month-line"
                aria-hidden="true"
              ></span>
              <span
                v-if="index === months.length - 1"
                class="c-workflow-bio__month-line c-workflow-bio__month-line--edge"
                aria-hidden="true"
              ></span>
            </template>
          </div>

          <div class="c-workflow-bio__chart-body">
            <div v-if="phases.length" class="c-workflow-bio__phases" :style="phaseGridStyle">
              <article
                v-for="phase in phases"
                :key="phase.id || phase.fields.title?.value"
                class="c-workflow-bio__phase"
                :class="getVariantClass(phase.fields.colorVariant?.value, 'phase')"
                :style="getPhaseStyle(phase)"
              >
                {{ phase.fields.title.value }}
              </article>
            </div>

            <RichText
              v-if="fields.timelineNote?.value"
              class="c-workflow-bio__timeline-note"
              :field="fields.timelineNote"
            />
          </div>
        </div>

        <div
          v-if="fields.cardTitle?.value || fields.cardBullets?.value || fields.bottomTitle?.value"
          class="c-workflow-bio__content"
        >
          <div
            v-if="fields.cardTitle?.value || fields.cardBullets?.value"
            class="c-workflow-bio__panel"
          >
            <h3 v-if="fields.cardTitle?.value" class="c-workflow-bio__panel-title">
              {{ fields.cardTitle.value }}
            </h3>
            <RichText
              v-if="fields.cardBullets?.value"
              class="c-workflow-bio__panel-list"
              :field="fields.cardBullets"
            />
          </div>

          <h3 v-if="fields.bottomTitle?.value" class="c-workflow-bio__bottom-title">
            {{ fields.bottomTitle.value }}
          </h3>
          <RichText
            v-if="fields.bottomDescription?.value"
            class="c-workflow-bio__bottom-description"
            :field="fields.bottomDescription"
          />
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  WorkflowBioMonthFields,
  WorkflowBioPhaseFields,
  WorkflowBioProps,
} from '@/types/components/WorkflowBio';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowBioMonth = ScItem<WorkflowBioMonthFields> & { fields: WorkflowBioMonthFields };
type WorkflowBioPhase = ScItem<WorkflowBioPhaseFields> & { fields: WorkflowBioPhaseFields };

const props = defineProps<WorkflowBioProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-bio'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const months = computed<WorkflowBioMonth[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'months');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowBioMonth => Boolean(item?.fields?.label?.value))
    : [];
});

const phases = computed<WorkflowBioPhase[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'phases');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowBioPhase => Boolean(item?.fields?.title?.value))
    : [];
});

const timelineStyle = computed(() => ({
  '--workflow-month-count': `${Math.max(months.value.length, 1)}`,
}));

const phaseGridStyle = computed(() => ({
  '--workflow-phase-columns': `${Math.max((months.value.length - 1) * 2, 1)}`,
}));

const toPositiveNumber = (value: number | string | undefined, fallback: number) => {
  const numericValue = Number(value ?? fallback);
  return Number.isFinite(numericValue) ? Math.max(1, numericValue) : fallback;
};

const COLOR_VALUE_PATTERN = /^(#|rgb\(|rgba\(|hsl\(|hsla\(|var\()/i;

const getColorValue = (value?: string) => {
  const color = value?.trim();
  return color && COLOR_VALUE_PATTERN.test(color) ? color : '';
};

const getMonthStyle = (month: WorkflowBioMonth) => {
  const color = getColorValue(month.fields.colorVariant?.value);
  return color ? { '--month-bg': color } : {};
};

const getPhaseStyle = (phase: WorkflowBioPhase) => {
  const startIndex = toPositiveNumber(phase.fields.startIndex?.value, 1);
  const span = toPositiveNumber(phase.fields.span?.value, 1);
  const row = toPositiveNumber(phase.fields.row?.value, 1);
  const color = getColorValue(phase.fields.colorVariant?.value);

  return {
    '--phase-start': `${startIndex}`,
    '--phase-span': `${span}`,
    '--phase-row': `${row}`,
    ...(color ? { '--phase-bg': color } : {}),
  };
};

const normalizeVariant = (variant?: string) => (variant || 'default').trim().toLowerCase();

const getVariantClass = (variant: string | undefined, block: 'month' | 'phase') =>
  getColorValue(variant) ? '' : `c-workflow-bio__${block}--${normalizeVariant(variant)}`;
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-workflow-bio {
  padding: 42px 0 48px;
  background: #fff;
  color: #3d3d3f;

  &__inner {
    width: 100%;
  }

  &__title {
    margin: 0;
    color: #3d3d3f;
    font-size: 24px;
    line-height: 1.18;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0;
  }

  &__chart {
    margin-top: 30px;
  }

  &__months {
    display: grid;
    grid-template-columns: 42px;
    gap: 10px;
    align-items: center;
    justify-content: center;
  }

  &__month {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: var(--month-bg, #cbd8cc);
    color: #fff;
    font-size: 14px;
    line-height: 1;
    font-weight: 700;
  }

  &__month--green {
    --month-bg: #6f9877;
  }

  &__month--sand {
    --month-bg: #ddc9a8;
  }

  &__month--gray {
    --month-bg: #d4d9df;
  }

  &__month-line {
    display: none;
  }

  &__chart-body {
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
    margin-top: 24px;
  }

  &__phases {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  &__phase {
    display: flex;
    align-items: center;
    min-height: 38px;
    padding: 7px 30px 7px 16px;
    background: var(--phase-bg, #f2f2f2);
    clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 50%, calc(100% - 22px) 100%, 0 100%);
    color: #6f9877;
    font-size: 15px;
    line-height: 1.16;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__phase--sand {
    --phase-bg: #e9dfcd;
    color: #3d3d3f;
  }

  &__phase--lightgreen {
    --phase-bg: #e7efdc;
  }

  &__timeline-note {
    color: #6f9877;
    font-size: 14px;
    line-height: 1.25;
    text-align: center;
  }

  &__timeline-note p {
    margin: 0;
  }

  &__content {
    margin-top: 4px;
    padding: 20px 18px 24px;
    border-radius: 12px;
    background: #f5faf8;
  }

  &__panel {
    padding: 20px 18px;
    border-radius: 12px;
    background: #fff;
  }

  &__panel-title {
    margin: 0 0 16px;
    color: #3d3d3f;
    font-size: 17px;
    line-height: 1.25;
    font-weight: 700;
  }

  &__panel-list {
    padding: 15px 17px;
    border-radius: 8px;
    background: #eef2ef;
    color: #3d3d3f;
    font-size: 14px;
    line-height: 1.45;
  }

  &__panel-list ul {
    display: grid;
    gap: 7px;
    margin: 0;
    padding-left: 18px;
  }

  &__panel-list p {
    margin: 0;
  }

  &__bottom-title {
    margin: 22px 0 16px;
    color: #3d3d3f;
    font-size: 20px;
    line-height: 1.2;
    font-weight: 700;
  }

  &__bottom-description {
    color: #111;
    font-size: 15px;
    line-height: 1.45;
  }

  &__bottom-description p {
    margin: 0;
  }

  @include tablet {
    padding: 48px 0 56px;

    &__title {
      font-size: 30px;
    }

    &__chart {
      position: relative;
      margin-top: 34px;
    }

    &__months {
      display: flex;
      align-items: center;
      justify-content: center;
      width: min(100%, 1040px);
      margin: 0 auto;
      gap: 0;
    }

    &__month {
      width: 40px;
      height: 40px;
      flex: 0 0 40px;
      font-size: 13px;
    }

    &__month-line {
      display: block;
      height: 2px;
      margin: 0 13px;
      flex: 1 1 58px;
      max-width: 68px;
      background: #6f9877;
    }

    &__chart-body {
      position: relative;
      display: block;
      width: min(100%, 1060px);
      min-height: 300px;
      margin: 34px auto 0;
    }

    &__phases {
      display: grid;
      grid-template-columns: repeat(var(--workflow-phase-columns), minmax(0, 1fr));
      gap: 4px 0;
    }

    &__phase {
      grid-column: var(--phase-start) / span var(--phase-span);
      grid-row: var(--phase-row);
      justify-content: center;
      min-height: 32px;
      padding: 6px 28px 6px 16px;
      font-size: 14px;
      text-align: center;
      clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%);
    }

    &__timeline-note {
      position: absolute;
      top: 8px;
      right: -76px;
      width: 190px;
      font-size: 13px;
      text-align: left;
    }

    &__content {
      margin-top: 4px;
      padding: 24px 28px 28px;
      border-radius: 14px;
    }

    &__panel {
      padding: 24px 28px;
      border-radius: 14px;
    }

    &__panel-title {
      font-size: 18px;
    }

    &__panel-list {
      padding: 16px 20px;
      font-size: 14px;
    }

    &__bottom-title {
      margin-top: 22px;
      font-size: 22px;
    }
  }

  @include tablet-landscape {
    padding: 58px 0 64px;

    &__title {
      font-size: 32px;
    }

    &__chart {
      margin-top: 38px;
    }

    &__months {
      width: min(100%, 1120px);
    }

    &__month {
      width: 42px;
      height: 42px;
      flex-basis: 42px;
      font-size: 14px;
    }

    &__month-line {
      margin: 0;
      max-width: 74px;
      flex-basis: 64px;
    }

    &__chart-body {
      width: min(100%, 1120px);
      min-height: 320px;
      margin-top: 36px;
    }

    &__phase {
      min-height: 34px;
      font-size: 16px;
    }

    &__timeline-note {
      top: 10px;
      right: -76px;
      font-size: 14px;
    }

    &__content {
      max-width: 1220px;
      margin-right: auto;
      margin-left: auto;
      padding: 28px 30px 30px;
      border-radius: 14px;
    }

    &__panel {
      padding: 26px 28px;
      border-radius: 14px;
    }

    &__panel-list {
      padding: 17px 24px;
    }
  }
}
</style>
